import React, { FC, useState } from 'react'
import { graphql, Link, useStaticQuery } from 'gatsby'
import Image from '../../components/Image/Image'
import { cls } from '../../utils/utils'
import { ROUTE_PATH } from '../../utils/constants'
import HeaderMenu from './HeaderMenu/HeaderMenu'
import { IHeaderData, IHeaderFlag, IHeaderTemplateProps } from './Header.types'
import styles from './Header.module.scss'
import { headerMenuItems } from './HeaderMenu/HeaderMenu.constants'

const OpenCloseButtons: FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <>
      <button className={styles.hamburgerButton} onClick={onClick}>
        Open menu
        <span className={styles.line}></span>
        <span className={styles.line}></span>
        <span className={styles.line}></span>
      </button>
      <button className={styles.closeMenuButton} onClick={onClick}>
        Close menu
        <span className={styles.line}></span>
        <span className={styles.line}></span>
      </button>
    </>
  )
}

const LanguageLinks: FC<{ flags: IHeaderFlag[] }> = ({ flags }) => {
  return (
    <div className={styles.languageLinks}>
      {flags.map((flag) => (
        <a href={flag.link} key={flag.link} target="_blank" rel="noopener">
          <Image image={flag.icon} alt={flag.altText} className={styles.languageLinkImage} />
        </a>
      ))}
    </div>
  )
}

const focusVisibleButton = (menuOpened: boolean): void => {
  const elements = document.getElementsByClassName(menuOpened ? styles.closeMenuButton : styles.hamburgerButton)
  if (elements.length !== 1) {
    return
  }

  const [element] = elements

  if (element instanceof HTMLButtonElement) {
    element.focus()
  }
}

const SectionMenu: FC<{ link: string }> = ({ link }) => (
  <div className={cls(styles.sectionMenu, 'mobile-hide')}>
    <div className={cls(styles.sectionMenuContainer, 'main-container')}>
      {headerMenuItems
        .find((item) => item.link.path === link)
        ?.subitems?.map((subItem, key) => (
          <Link to={subItem.path} className={styles.subItem} key={key} activeClassName={styles.subItemActive}>
            <div className={styles.subItemText}>{subItem.name}</div>
          </Link>
        ))}
    </div>
  </div>
)

export const HeaderTemplate: FC<IHeaderTemplateProps> = ({ logo, logoAltText, flags, sectionsLink, className }) => {
  const [menuOpened, setMenuOpened] = useState<boolean>(false)

  const toggleHamburgerMenu = (): void => {
    setMenuOpened(!menuOpened)

    window.setTimeout(() => focusVisibleButton(!menuOpened))
  }

  return (
    <>
      <header id="header" className={cls(styles.header, menuOpened ? styles.menuOpen : styles.menuClosed, className)}>
        <div className={cls(styles.headerContent, 'main-container')}>
          <div className={styles.logoAndHamburger}>
            <div className={styles.logo}>
              <Link to={ROUTE_PATH.main.path}>
                <Image image={logo} alt={logoAltText} className={styles.logoImage} />
              </Link>
            </div>
            <OpenCloseButtons onClick={toggleHamburgerMenu} />
          </div>

          <div className={styles.menuAndLanguages}>
            <div className={styles.menu}>
              <HeaderMenu />
            </div>

            <LanguageLinks flags={flags} />
          </div>
        </div>
      </header>
      {sectionsLink && <SectionMenu link={sectionsLink} />}
    </>
  )
}

const Header: FC<{ headerLink?: string; className?: string }> = ({ headerLink, className }) => {
  const headerData: IHeaderData = useStaticQuery(query)
  const data = headerData.header.frontmatter

  return <HeaderTemplate {...data} sectionsLink={headerLink} className={className} />
}

export const query = graphql`
  query HeaderData {
    header: markdownRemark(frontmatter: { componentName: { eq: "Header" } }) {
      frontmatter {
        logo {
          publicURL
          childImageSharp {
            gatsbyImageData(layout: FIXED)
          }
        }
        logoAltText
        flags {
          link
          altText
          icon {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FIXED)
            }
          }
        }
      }
    }
  }
`

export default Header
